import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getSocket, initSocket } from '../socket/client';
import { useMessages } from '../hooks/useChat';
import { useNotify } from '@/notifications/NotificationProvider';
import { Button } from './ui/button';

type Message = {
    _id: string;
    conversation: string;
    sender: string;
    text: string;
    attachments?: any[];
    readBy: string[];
    createdAt: string;
};

export default function ChatWindow({
    token,
    conversationId,
    currentUserId,
    counterpart
}: {
    token: string;
    conversationId: string;
    currentUserId: string; // pass current user id
    counterpart?: { _id: string; firstName?: string; lastName?: string; email?: string; phone?: string; avatar?: string };
}) {
    const {notify} = useNotify();
    const [text, setText] = useState('');
    const { messages, mutate } = useMessages(conversationId);
    const listRef = useRef<HTMLDivElement>(null);

    // Join room, listen to events, mark as read
    useEffect(() => {
        const sock = initSocket(token);

        const afterConnect = () => {
            sock.emit('conversation:join', conversationId);
            // mark all as read on open
            sock.emit('message:read', { conversationId });
        };

        if (sock.connected) afterConnect();
        else sock.once('connect', afterConnect);

        const onNew = (msg: any) => {
            if (msg.conversation === conversationId) {
                mutate(); // refresh messages
                // auto mark read after receiving
                sock.emit('message:read', { conversationId });
            }
        };
        const onReadUpdate = (payload: any) => {
            if (payload.conversationId === conversationId) {
                // revalidate to update readBy locally
                mutate();
            }
        };

        sock.on('message:new', onNew);
        sock.on('message:read:update', onReadUpdate);

        return () => {
            sock.off('message:new', onNew);
            sock.off('message:read:update', onReadUpdate);
        };
    }, [token, conversationId, mutate]);

    useEffect(() => {
        listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
    }, [messages]);

    const send = async () => {
        const trimmed = text.trim();
        if (!trimmed) return;

        const sock = getSocket() || initSocket(token);
        // ensure joined (safe to re-emit)
        sock.emit('conversation:join', conversationId);

        sock.emit('message:send', { conversationId, text: trimmed }, (res: any) => {
            if (res?.ok) {
                setText('');
                // optimistic scroll; server will push message:new
            } else {
                console.error('send error:', res?.error);
            }
        });
    };

    // Compute seen status for my messages:
    // read if every other participant (besides me) is in readBy
    // You can pass participants into this component if needed; for now assume counterpart is the only other party.
    const computeStatus = (m: Message) => {
        const isMine = m.sender === currentUserId;
        if (!isMine) return { isMine, delivered: true, read: m.readBy?.includes(currentUserId) || false }; // for incoming, read indicator is less relevant
        const otherId = counterpart?._id;
        const delivered = true; // message created on server => delivered
        const read = otherId ? m.readBy?.includes(otherId) : false;
        return { isMine, delivered, read };
    };

    return (
        <div className="flex flex-col border rounded" style={{ height: 650 }}>
            {/* Header (optional) */}
            <div className="p-3 border-b flex items-center gap-3">
                {counterpart?.avatar ? (
                    <img src={counterpart.avatar} className="w-8 h-8 rounded-full object-cover" alt="" />
                ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-300" />
                )}
                <div className="font-semibold">{counterpart?.firstName && `${counterpart.firstName} ${counterpart.lastName}` || 'Chat'}</div>
            </div>

            {/* Messages */}
            <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50" style={{ height: 300 }}>
                {messages.map((m: any) => {
                    const { isMine, delivered, read } = computeStatus(m);
                    return (
                        <div key={m._id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[75%] rounded-2xl px-3 py-2 shadow-sm ${isMine ? 'bg-green-500 text-white rounded-br-sm' : 'bg-white text-gray-900 rounded-bl-sm'}`}>
                                {/* Optional: show counterpart name for group chat / multiple sellers */}
                                {!isMine && counterpart?.firstName && (
                                    <div className="text-xs text-gray-500 mb-1">{counterpart.firstName}</div>
                                )}
                                <div className="whitespace-pre-wrap break-words">{m.text}</div>
                                <div className={`mt-1 flex items-center gap-1 text-[10px] ${isMine ? 'text-white/80' : 'text-gray-500'}`}>
                                    <span>{new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                    {isMine && (
                                        <span className="ml-1">
                                            {read ? '✓✓' : delivered ? '✓' : ''}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
                {messages.length === 0 && (
                    <div className="text-center text-sm text-gray-500 py-8">Say hi 👋</div>
                )}
            </div>

            {/* Composer */}
            <div className="p-2 border-t flex gap-2 bg-white">
                <input
                    className="flex-1 border rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={text}
                    onChange={e => setText(e.target.value)}
                    placeholder="Type a message"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            send();
                        }
                    }}
                />
                <Button onClick={send} className="px-4 rounded-full">Send</Button>
            </div>
        </div>
    );
}
